/**
 * Shared VO / living-proposal catalogue presentation resolution.
 * Prefer explicit line values; never overwrite non-empty user edits with empties or weaker sources.
 */

import type { DtoolsReviseLine } from "./dtoolsProposalRevise";
import type { VoScopeLine } from "./variationOrderDiff";

export type CataloguePresentationSeed = {
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  catalogProductId?: number | null;
  projectCatalogueId?: number | null;
  catalogueKey?: string | null;
  customerExplanation?: string | null;
};

/** First non-empty trimmed string wins. */
export function coalesceNonEmpty(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) return t;
  }
  return null;
}

export function coalescePositiveId(
  ...candidates: Array<number | null | undefined>
): number | null {
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Fill empty fields on target from seed; never replace a non-empty target value. */
export function mergePresentationPreferExisting<T extends CataloguePresentationSeed>(
  target: T,
  seed: CataloguePresentationSeed | null | undefined,
): T {
  if (!seed) return target;
  return {
    ...target,
    brand: coalesceNonEmpty(target.brand, seed.brand),
    model: coalesceNonEmpty(target.model, seed.model),
    name: coalesceNonEmpty(target.name, seed.displayName, seed.name) ?? target.name,
    displayName: coalesceNonEmpty(target.displayName, seed.displayName, seed.name),
    description: coalesceNonEmpty(target.description, seed.description),
    imageUrl: coalesceNonEmpty(target.imageUrl, seed.imageUrl),
    catalogueKey: coalesceNonEmpty(target.catalogueKey, seed.catalogueKey),
    customerExplanation: coalesceNonEmpty(target.customerExplanation, seed.customerExplanation),
    catalogProductId: coalescePositiveId(target.catalogProductId, seed.catalogProductId),
    projectCatalogueId: coalescePositiveId(target.projectCatalogueId, seed.projectCatalogueId),
  };
}

/**
 * Resolve VO scope presentation fields from a revise/staging line.
 * Uses manufacturer / polishedDescription (not the non-existent brand/description aliases).
 */
export function presentationFieldsFromReviseLine(line: DtoolsReviseLine): CataloguePresentationSeed {
  const brand = coalesceNonEmpty(
    (line as { brand?: string | null }).brand,
    line.manufacturer,
  );
  const description = coalesceNonEmpty(
    line.proposalDescriptionOverride,
    line.polishedDescription,
    line.cleoDescription,
    line.dtoolsDescription,
    (line as { description?: string | null }).description,
  );
  const imageUrl = coalesceNonEmpty(
    line.proposalImageOverride,
    line.imageUrl,
    (line as { imageUrlSnapshot?: string | null }).imageUrlSnapshot,
  );
  return {
    brand,
    model: coalesceNonEmpty(line.model),
    displayName: coalesceNonEmpty(line.displayName),
    name: coalesceNonEmpty(line.displayName, line.model, brand),
    description,
    imageUrl,
    catalogProductId: coalescePositiveId(line.catalogProductId),
    projectCatalogueId: coalescePositiveId(line.projectCatalogueId),
    catalogueKey: coalesceNonEmpty(line.catalogueKey),
    customerExplanation: coalesceNonEmpty(line.customerExplanation),
  };
}

export function applyPresentationToVoScopeLine(
  line: VoScopeLine,
  presentation: CataloguePresentationSeed,
): VoScopeLine {
  const merged = mergePresentationPreferExisting(
    {
      brand: line.brand,
      model: line.model,
      name: line.name,
      displayName: line.displayName,
      description: line.description,
      imageUrl: line.imageUrl,
      catalogProductId: line.catalogProductId,
      projectCatalogueId: line.projectCatalogueId,
      catalogueKey: line.catalogueKey,
      customerExplanation: line.customerExplanation,
    },
    presentation,
  );
  return {
    ...line,
    brand: merged.brand ?? null,
    model: merged.model ?? null,
    name: coalesceNonEmpty(merged.name, line.name) ?? line.name,
    description: merged.description ?? null,
    displayName: merged.displayName ?? null,
    imageUrl: merged.imageUrl ?? null,
    catalogProductId: merged.catalogProductId ?? null,
    projectCatalogueId: merged.projectCatalogueId ?? null,
    catalogueKey: merged.catalogueKey ?? null,
    customerExplanation: merged.customerExplanation ?? null,
  };
}

/** Lookup key for catalogue enrich — brand+model when both exist, else model. */
export function cataloguePresentationLookupKey(input: {
  brand?: string | null;
  model?: string | null;
}): string | null {
  const model = coalesceNonEmpty(input.model);
  if (!model) return null;
  const brand = coalesceNonEmpty(input.brand);
  return brand ? `${brand.toLowerCase()}::${model.toLowerCase()}` : `::${model.toLowerCase()}`;
}
